import type { AgingSide, SettlementAging } from '../api';
import type { Dictionary, Locale } from '../i18n';
import { amount as money, date as written } from '../format';
import type { Panel } from './Shell';

/**
 * Wiekowanie rozrachunków na pulpicie (E12-S1) — dla firm na pełnych księgach.
 *
 * Dwa wiersze: należności i zobowiązania, każdy z liczbą otwartych i po terminie oraz kwotami
 * w przedziałach. Liczby pochodzą z rozrachunków w dzienniku (E7-S4), nie z listy faktur —
 * przy pełnych księgach to dziennik jest prawdą o tym, kto komu ile jest winien.
 */

const BUCKETS = ['NOT_DUE', 'DAYS_1_30', 'DAYS_31_60', 'DAYS_61_90', 'OVER_90'] as const;

function Row({ label, side, t, locale }: { label: string; side: AgingSide; t: Dictionary; locale: Locale }) {
  return (
    <tr>
      <td className="wrap">
        <b>{label}</b>
        <div className="hint">
          {side.count} {t.dashboard.agingOpen}
          {side.overdueCount > 0 && (
            <>
              {' · '}
              <span className="chip st-bad">
                {side.overdueCount} {t.dashboard.agingOverdue} · {money(side.overdueTotal, locale)}
              </span>
            </>
          )}
        </div>
      </td>
      {BUCKETS.map((bucket) => (
        <td key={bucket} className="n mono">
          {money(side.byBucket[bucket] ?? '0', locale)}
        </td>
      ))}
      <td className="n mono">
        <b>{money(side.total, locale)}</b>
      </td>
    </tr>
  );
}

export function AgingPanel({
  aging,
  t,
  locale,
  onGo,
}: {
  aging: SettlementAging;
  t: Dictionary;
  locale: Locale;
  onGo?: (panel: Panel) => void;
}) {
  return (
    <div className="panel">
      <div className="hd">
        <div>
          <h4>{t.dashboard.aging}</h4>
          <p>{t.dashboard.agingLead(written(aging.asOf, locale))}</p>
        </div>
        {onGo && (
          <button type="button" className="btn ghost sm" onClick={() => onGo('journal')}>
            {t.dashboard.goToJournal}
          </button>
        )}
      </div>
      <div className="tbl">
        <table>
          <thead>
            <tr>
              <th />
              {BUCKETS.map((bucket) => (
                <th key={bucket} className="n">
                  {t.journal.bucket[bucket]}
                </th>
              ))}
              <th className="n">{t.journal.total}</th>
            </tr>
          </thead>
          <tbody>
            <Row label={t.dashboard.agingReceivables} side={aging.receivables} t={t} locale={locale} />
            <Row label={t.dashboard.agingPayables} side={aging.payables} t={t} locale={locale} />
          </tbody>
        </table>
      </div>
    </div>
  );
}
